import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { globalVariables } from "helper/globalVariables";
import { TextButton } from "shared/Components/textButton";
import { Button } from "shared/Components/button";
import { TextInput } from "shared/Components/textInput";
import Dropdown from "shared/Components/dropdown";
import { SearchDropdown } from "shared/Components/searchDropdown";
import Modal from "shared/Components/modal";
import { pending, success } from "helper/icons";
import {
  getAllRegisteredDogsQuery,
  addManualDrawMutation,
} from "redux/features/superAdminSlice/manageDogsSlice/manageDogsGql";
import {
    getSearchByTrialCalendarIdQuery,
  } from "redux/features/trialCalendar/trialCalendarGql";
import { transformArrayToPremier } from "helper/utils";
import { orderSearchesElements } from "helper/orderSearchElements";
import { client } from "helper/apolloConfig";
import { dropdowns } from "container/TrialCalendar/DogSignUp/Components/constant";

export function AddEntriesModal({ trialId, modalRef, setManualEntriesData }) {

    const [isModalClosed, setIsModalClosed] = useState(false);

    const [apiData, setApiData] = useState({
        title: "",
        desc: "",
        type: "pending",
    });

    const [checkValidation, setCheckValidation] = useState(false);
    const defaultState = {
        authId: "",
        data: {
          dogId: null,
          dog: "",
          breed: "",
          division: "",
          element: "",
          trialCalendarId: null,
        },
      };

    const [entriesData, setEntriesData] = useState(defaultState);

    const [searchContainersData, setSearchContainersData] = useState([]);
    const [dog, setDog] = useState({
        authId: "",
        name: "",
        memberName: "",
    })
    const [getData, { loading, error, data }] = useLazyQuery(
        getAllRegisteredDogsQuery,
        { fetchPolicy: "network-only" }
    );

    const getSearchContainersDropdown = async () => {
        // Don't fetch if trialId is not available
        if (!trialId) return;

        try {
            const result = await client.query({
                query: getSearchByTrialCalendarIdQuery,
                variables: {
                    trialCalendarId: parseInt(trialId),
                },
            });

            const data = result?.data?.getSearchByTrialCalendarId;
            if (data) {
            const searchContainers = data?.map((e) => ({
                id: e?.id,
                label: e?.searchName,
                element: e?.element, // underlying element value for backend validation
            }));
            setSearchContainersData(orderSearchesElements(searchContainers, "label"));
            }
        } catch (e) {}
    };

    const getAllDogs = data?.getAllRegisteredDogs.data;
    
    const handleDogSearchData = ({ search = "" }) => {
        getData({
            variables: {
            pageNumber: 1,
            pageSize: 20,
            search,
            },
        });
    };

    useEffect(() => {
        getSearchContainersDropdown();
        handleDogSearchData({});
    }, [trialId]);

    useEffect(()=> {
        if (isModalClosed) {
            setDog({
                authId: "",
                name: "",
                ownerName: "",
            });
            setApiData({
                ...apiData,
                type: "pending",
            });
            setEntriesData(defaultState)
            setIsModalClosed(false);
        }
      }, [isModalClosed])

    const dogOptions = getAllDogs?.map((dog) => {
        return dog.call_name;
    });

    const onSelectedDog = (option) => {
        // Find the matching dog by call_name — use index 0 of the filtered result,
        // NOT the dropdown index which points into the full unfiltered list.
        const selected = getAllDogs.find((dog) => dog.call_name === option);
        if (selected) {
            setDog({
                authId: selected.authId,
                name: selected.call_name,
                memberName: selected.owner_name,
            });
            setEntriesData((prev) => ({
                ...prev,
                authId: selected.authId,
                data: {
                    ...prev.data,
                    dogId: selected.dog_id,
                    dog: selected.call_name,
                    memberName: selected.owner_name,
                    breed: selected.breed_mix || selected.breed,
                    trialCalendarId: parseInt(trialId, 10)
                },
            }));
        } else {
            // Nothing matched — clear everything so no previous dog ghost-rides
            setDog({ authId: "", name: "", memberName: "" });
            setEntriesData(defaultState);
        }
    };

    const onChangeValue = (value, name) => {
        setEntriesData((prev) => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: value,
            },
        }));
    };
    
    const handleAddEntries = async () => {
        const { authId, data } = entriesData;
        if (!authId || !data.division.trim() || !data.element.trim() || data.dogId === null) {
            setCheckValidation(true);
            return;
        }
        try {
            const result = await client.mutate({
                mutation: addManualDrawMutation,
                variables: {
                    authId,
                    dogId: data.dogId,
                    division: data.division,
                    elements: data.element,
                    searchId: data.searchId ?? null,
                    trialCalendarId: data.trialCalendarId,
                },
            });
            const manualEntryData = {
                id: result.data.addManualDraw[0].id,
                ...data,
                // Use the dropdown's display label (with Room 2 suffix) so the
                // optimistic row matches what the next refetch will show.
                element: data.displayElement || data.element,
            };
            setManualEntriesData(prev => [manualEntryData, ...prev]);
            setApiData({
                ...apiData,
                title: globalVariables.success,
                desc: `Trial entry for ${dog.name} has been added successfully`,
                type: "success",
            });
        } catch (e) {
            setApiData({
                ...apiData,
                title: globalVariables.error,
                desc: e.message,
                type: "error",
            });
        }
    };
    
    if (apiData.type == "error" || apiData.type == "success") {
        return (
            <Modal setIsModalClosed={setIsModalClosed} extraClass={`pt-0`} headerClass="sticky top-6 left-100 z-50" iconPosition="right-0" ref={modalRef}>
                <div className="w-[400px] mt-4 flex flex-col items-center gap-4 text-center">
                  <img src={apiData.type == "error" ? pending : success} alt="" className="w-12 h-12" />
                  <div className="text-xl font-semibold text-gray-900">
                    {apiData.title}
                  </div>
                  <div className="text-md font-regular text-gray-700">
                    {apiData.desc}
                  </div>
                  {apiData.type !== "success" && (
                    <Button 
                        onClick={() => {
                            setApiData({
                                ...apiData,
                                type: "pending",
                            });
                        }} 
                        label="Update Element" 
                    />
                  )}
                </div>
            </Modal>
        );
    }

   return (
    <Modal extraClass={`pt-0`} headerClass="sticky top-6 left-100 z-50" iconPosition="right-0" ref={modalRef}>
        <div className="m-auto w-[400px] mt-8 pt-6">
          <div className="flex flex-col gap-4 border rounded-md shadow-100 p-4">
            <SearchDropdown 
                width="w-[100%]"
                dropdownOptions={dogOptions}
                placeholder={globalVariables.dogName}
                onChangeSearchText={(value) => handleDogSearchData({ search: value })}
                onSelectOption={onSelectedDog}
                errorMsg={checkValidation}
                defaultValue={dog.name}
            />
            <TextInput
                type="text"
                value={dog.memberName}
                placeholder="Owner Name"
                disabled={true}
            />
            <Dropdown
                width="w-[100%]"
                dropdownOptions={transformArrayToPremier(searchContainersData).map(dog => dog?.label) ?? []}
                placeholder={globalVariables.selectSearch}
                checkValidation={checkValidation}
                onChange={label => {
                    const matched = searchContainersData.find(s => s.label === label);
                    // Send the underlying element for qualification checks,
                    // and the searchId so the backend can tell Room 1 from Room 2.
                    setEntriesData(prev => ({
                        ...prev,
                        data: {
                            ...prev.data,
                            element: matched?.element ?? label,
                            searchId: matched?.id ?? null,
                            // Keep the human-facing label (e.g. "Novice Interiors 2")
                            // so the optimistic row shows the room-number suffix.
                            displayElement: matched?.label ?? label,
                        },
                    }));
                }}
                defaultOption={entriesData.data.element}
             />
             {dropdowns?.map((dropdown, index) => (
              <React.Fragment key={index}>
                <Dropdown
                  dropdownOptions={dropdown.options ?? []}
                  width="w-[100%]"
                  dynamicHeight
                  placeholder={dropdown.placeHolder}
                  checkValidation={checkValidation}
                  onChange={value => onChangeValue(value, dropdown.name)}
                  defaultOption={entriesData.data.division}
                />
              </React.Fragment>
            ))}
            <div className="flex justify-end">
                <TextButton onClick={handleAddEntries} label="Add" />
            </div>
          </div>
      </div>
    </Modal>
  );
}
